import {
  ADMIN_USER_TYPE,
  ADMIN_VIEW_USER_TYPE,
  CONFIG_ADMIN_USER_TYPE,
  CONFIG_ADMIN_VIEW_USER_TYPE,
  KWH,
  MEMBER_USER_TYPE,
  MEMBER_VIEW_USER_TYPE,
  MW,
  NO_DATA,
  RESOURCE_TYPE_DASHBOARD,
  RP_CONFIGURATION,
} from 'consts';
import useFormatter from 'hook/useFormatter';
import { useFilterHook, useKpiHook, useWidgetHook } from 'hook/usePageConfig';

export const allSearchBarFilters = ({
  t, epoch, userType, energyCommunities, filters, setFilters, currentTemplate, setCurrentTemplate,
}) => {
  const {
    currentTimeZone,
  } = epoch ?? {};

  const {
    energyCommunityFilter: selectedEc,
    configurationFilter: configSelected,
    podFilter: podSelected,
  } = filters;

  const {
    configOptions,
    dateFilter,
    memberArr,
  } = useFilterHook({
    energyCommunities,
    selectedEc,
    configSelected,
    podSelected,
    t,
    epoch,
    currentTimeZone,
    setFilters,
    filters,
    currentTemplate,
    setCurrentTemplate,
  });

  let toRet = [];

  if (userType === ADMIN_USER_TYPE || userType === ADMIN_VIEW_USER_TYPE) {
    toRet = [dateFilter];
  }

  if (userType === CONFIG_ADMIN_USER_TYPE || userType === CONFIG_ADMIN_VIEW_USER_TYPE) {
    toRet = [
      {
        id: RP_CONFIGURATION,
        type: 'select',
        label: t('filters.configuration'),
        options: configOptions,
        disableChipClose: true,
        defaultValue: configSelected,
      },
      dateFilter,
    ];
  }

  if (userType === MEMBER_USER_TYPE || userType === MEMBER_VIEW_USER_TYPE) {
    toRet = memberArr;
  }
  return toRet;
};

export const allWidgetStructure = ({
  t, widgetsData, theme,
  isLoading, countryID, granularity, setGranularity,
  dataChartType, setDataChartType, userType, isDashboard, isDateDiffUnder30Days,
}) => {
  const isMember = userType === MEMBER_USER_TYPE || userType === MEMBER_VIEW_USER_TYPE;

  const newWidgetData = {
    ...widgetsData,
    generation: isMember ? widgetsData?.configGeneration : widgetsData?.generation,
    consumption: isMember ? widgetsData?.configConsumption : widgetsData?.consumption,
    shared: isMember ? widgetsData?.configShared : widgetsData?.shared,
  };

  const {
    rewardsWidget,
    energyProductionWidget,
    energyPerformanceWidget,
  } = useWidgetHook({
    t,
    widgetsData: newWidgetData,
    theme,
    isLoading,
    countryID,
    granularity,
    setGranularity,
    dataChartType,
    setDataChartType,
    userType,
    isDashboard,
    isDateDiffUnder30Days,
  });

  let toRet = [];

  if (userType === ADMIN_USER_TYPE || userType === ADMIN_VIEW_USER_TYPE
    || userType === CONFIG_ADMIN_USER_TYPE || userType === CONFIG_ADMIN_VIEW_USER_TYPE) {
    toRet = [rewardsWidget, energyProductionWidget];
  }

  if (userType === MEMBER_USER_TYPE || userType === MEMBER_VIEW_USER_TYPE) {
    toRet = [energyPerformanceWidget, energyProductionWidget];
  }

  return toRet;
};

export const allKpisStructure = ({
  t, isFetchingRevenue, processedData, isWidgetAvailable, countryID, userType,
}) => {
  const { unit: { formatElectricEnergy, formatPower }, currency: { formatCurrency } } = useFormatter();

  const {
    updateDataKpi,
    totalMembersKpi,
    totalSharedEnergyKpi,
    totalPowerKpi,
    revenueKpi,
    saturationKpi,
    totalConfigurationsKpi,
    savedCo2Kpi,
    totalPlantsKpi,
    totalConsumptioKpi,
    totalGenerationKpi,
  } = useKpiHook({
    t, processedData, isWidgetAvailable, countryID, isFetchingRevenue, resource: RESOURCE_TYPE_DASHBOARD,
  });

  let toRet = [];

  const totalMembersValue = `${processedData?.members ?? NO_DATA} ${t('kpi.members')}`;

  const totalSharedEnergyValue = formatElectricEnergy(processedData?.shared, KWH, 0);

  const totalRevenueValue = formatCurrency(processedData?.rewards, 0);

  const totalSaturationValue = `${processedData?.saturation ? processedData.saturation
    .toFixed(2).replace('.', ',') : NO_DATA} %`;

  const totalPowerValue = formatPower(processedData?.totalPower, MW, MW);

  const totalSavedCo2Value = `${processedData?.savedCO2 ? processedData.savedCO2.toFixed(2).replace('.', ',') : NO_DATA} ton`;

  const kpiAdminArray = [
    totalMembersKpi(totalMembersValue, t('kpi.membersTooltip')),
    totalSharedEnergyKpi(totalSharedEnergyValue, t('kpi.totalSharedEnergyTooltip'), t('kpi.totalSharedEnergy')),
    revenueKpi(totalRevenueValue, t('kpi.totalRevenueTooltip'), t('kpi.totalRevenue')),
    saturationKpi(totalSaturationValue, t('kpi.saturationTooltip')),
    totalConfigurationsKpi(processedData?.totalConfig, t('kpi.configTooltip')),
    totalPowerKpi(totalPowerValue, t('kpi.totalPowerTooltip')),
    savedCo2Kpi(totalSavedCo2Value, t('kpi.co2Tooltip')),
    updateDataKpi,
  ];

  const kpiConfigAdminArray = [
    totalMembersKpi(totalMembersValue, t('kpi.membersTooltipConfig')),
    totalSharedEnergyKpi(totalSharedEnergyValue, t('kpi.totalSharedEnergyTooltipConfig'), t('kpi.totalSharedEnergy')),
    revenueKpi(totalRevenueValue, t('kpi.totalRevenueConfigTooltip'), t('kpi.totalRevenueConfig')),
    saturationKpi(totalSaturationValue, t('kpi.saturationConfigTooltip')),
    totalPlantsKpi(processedData?.totalPlants, t('kpi.totalPlantsTooltip')),
    totalPowerKpi(totalPowerValue, t('kpi.totalPowerTooltipConfig')),
    savedCo2Kpi(totalSavedCo2Value, t('kpi.co2Tooltip')),
    updateDataKpi,
  ];

  const kpiMemberArray = [
    totalConsumptioKpi(formatElectricEnergy(processedData?.memberConsumption, KWH, 0), t('kpi.totalConsumptionMemberTooltip')),
    totalSharedEnergyKpi(
      formatElectricEnergy(processedData?.memberShared, KWH, 0),
      t('kpi.totalSharedEnergyMemberTooltip'),
      t('kpi.totalMemberShared'),
    ),
    revenueKpi(totalRevenueValue, t('kpi.totalRevenueMemberTooltip'), t('kpi.totalMemberRevenue')),
    totalGenerationKpi(formatElectricEnergy(processedData?.memberGeneration, KWH, 0), t('kpi.totalMemberPowerTooltip')),
    savedCo2Kpi(totalSavedCo2Value, t('kpi.co2Tooltip')),
    updateDataKpi,
  ];

  if (userType === ADMIN_USER_TYPE || userType === ADMIN_VIEW_USER_TYPE) {
    toRet = kpiAdminArray;
  }
  if (userType === CONFIG_ADMIN_USER_TYPE || userType === CONFIG_ADMIN_VIEW_USER_TYPE) {
    toRet = kpiConfigAdminArray;
  }

  if (userType === MEMBER_USER_TYPE || userType === MEMBER_VIEW_USER_TYPE) {
    toRet = kpiMemberArray;
  }

  return toRet;
};
